import { faMinusCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, FormGroup, Input } from '@orobix/orobix-fe-components'
import { ArrayHelpers, FieldArray, useField, useFormikContext } from 'formik'
import { is } from 'ramda'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { ProductType } from '../../types'
import useStyles from './ClassesField.styles'
import PrebuiltClassesSelect from './PrebuiltClassesSelect/PrebuiltClassesSelect'

interface ClassesFieldProps {
  fieldName: string
  actionType: 'create' | 'update'
  max: number
  min: number
  disabled?: boolean
  productType?: ProductType
}

interface ClassFieldProps {
  fieldName: string
  fieldHelpers: ArrayHelpers
  index: number
  actionType: 'create' | 'update'
  max: number
  min: number
  disableRemoveFieldButton: boolean
  showError: boolean
  disabled?: boolean
  productType?: ProductType
}

const ClassField = ({
  fieldName,
  fieldHelpers,
  index,
  actionType,
  max,
  min,
  disableRemoveFieldButton,
  showError,
  disabled,
  productType,
}: ClassFieldProps) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const { remove } = fieldHelpers
  const { isSubmitting, handleBlur } = useFormikContext()

  const [field, meta, helpers] = useField<string>(fieldName)

  const { value } = field
  const { error, touched, initialValue } = meta
  const { setValue } = helpers

  useEffect(() => {
    if (initialValue) {
      setValue(initialValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className={classes.classesInputRow}>
      <div className={classes.classInput}>
        <Input
          data-testid="inputClass"
          name={`classes.${index}`}
          id={`classes.${index}`}
          type="text"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setValue(e.target.value)
          }
          onBlur={handleBlur}
          value={value}
          error={!!(error && touched && showError)}
          errorMessage={showError ? error : undefined}
          disabled={isSubmitting || actionType !== 'create' || disabled}
          noMarginBottom
        />
      </div>

      {max > 1 && !disabled && actionType === 'create' && (
        <Button
          data-testid="removeClassButton"
          onClick={() => remove(index)}
          label={t('Remove this class')}
          title={t('Remove this class')}
          hideLabel
          icon={<FontAwesomeIcon icon={faMinusCircle} />}
          disabled={disableRemoveFieldButton}
          buttonType="button"
          type="blank-destroy"
        />
      )}
    </div>
  )
}

const ClassesField = ({
  fieldName,
  actionType,
  max,
  min,
  disabled,
  productType,
}: ClassesFieldProps) => {
  const { t } = useTranslation()
  const { isSubmitting } = useFormikContext()

  const [field, meta, helpers] = useField<string[]>(fieldName)

  const { name, value } = field
  const { error, touched, initialValue } = meta
  const { setValue } = helpers

  const disableRemoveFieldButton =
    isSubmitting || actionType !== 'create' || value.length <= min

  const isGeneralError = is(String, error)

  useEffect(() => {
    if (!initialValue || initialValue.length === 0) {
      setValue(Array(min).fill(''))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [min])

  return (
    <div id="productClassesId">
      <FormGroup
        data-testid="fieldsetClasses"
        label={max > 1 ? t('Classes') : t('Class')}
        disabled={isSubmitting || actionType !== 'create'}
        helpMessage={
          actionType === 'create' && !disabled
            ? t(
                `Specify the class name. Warning, you can't change it once the product is created.`,
                { count: max }
              )
            : ''
        }
        error={!!(error && touched && isGeneralError)}
        errorMessage={isGeneralError ? error : undefined}
      >
        <FieldArray
          name={name}
          render={(props) => (
            <>
              {productType && actionType === 'create' && (
                <PrebuiltClassesSelect
                  fieldName="prebuiltClasses"
                  productType={productType}
                  props={props}
                  min={min}
                  max={max}
                />
              )}
              {value.map((c, i) => (
                <ClassField
                  fieldName={`${name}.${i}`}
                  fieldHelpers={props}
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  index={i}
                  actionType={actionType}
                  max={max}
                  min={min}
                  disableRemoveFieldButton={disableRemoveFieldButton}
                  showError={!isGeneralError}
                  disabled={disabled}
                />
              ))}

              {max > 1 && !disabled && actionType === 'create' && (
                <Button
                  data-testid="addClassButton"
                  onClick={() => props.push('')}
                  label={t('Add a class')}
                  disabled={
                    isSubmitting ||
                    actionType !== 'create' ||
                    value.length >= max
                  }
                  buttonType="button"
                  type="default"
                  block
                />
              )}
            </>
          )}
        />
      </FormGroup>
    </div>
  )
}

export default ClassesField
